import { useCallback, useEffect, useState } from 'react';
import { motion } from 'motion/react';
import { Briefcase, Plus } from 'lucide-react';
import { EVENTS } from '../../constants/navigation';
import { BusinessService } from '../../services/BusinessService';
import { MAX_BUSINESS_PROFILES, BusinessProfile } from '../../types/business';

/**
 * Business profiles are not ready to be used, so the create entry point is
 * hidden outright.
 *
 * Its backend is not merely unconfigured — none of its five Cloud Functions has
 * ever deployed. `createBusinessCheckoutSession`, `stripeWebhook`,
 * `onPurchaseCheckConfirmed`, `onBusinessProfileCreated` and
 * `onBusinessProfileDeleted` are all absent from production, because an unset
 * defineSecret blocks the whole functions codebase. So a user who tapped
 * through would reach a registration flow whose payment step cannot complete.
 *
 * Hidden rather than greyed out: a disabled "(coming soon)" row is a visible
 * promise of something that cannot be used, which is exactly the kind of
 * unfinished surface an App Store review flags. Nothing advertises the feature
 * until it works.
 *
 * Existing owners keep their "Manage <name>" rows: those read data that is
 * already there. Only creating a NEW profile is closed off.
 *
 * Flip this to true once the Stripe secrets are set and the functions deploy.
 */
const BUSINESS_PROFILES_ENABLED = false;

/**
 * The business rows in the profile's Settings list.
 *
 * One "Manage <name>" row per business this account owns, then a "Create a
 * Business Profile" row underneath while there are slots left. With no
 * businesses yet, that create row is the only thing here, so the entry point
 * looks identical to the other settings links until the moment it has something
 * to manage.
 *
 * Styling is passed in by the caller rather than defined here, so these rows
 * stay visually identical to App Settings / Safety & Privacy / Rules without
 * duplicating their classes — if that list is restyled, these follow.
 */
export function BusinessSettingsLinks({
  ownerId,
  rowClassName,
  iconClassName,
  labelClassName,
}: {
  ownerId: string;
  rowClassName: string;
  iconClassName: string;
  labelClassName: string;
}) {
  const [profiles, setProfiles] = useState<BusinessProfile[]>([]);
  const [slotsUsed, setSlotsUsed] = useState(0);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    if (!ownerId) return;
    const [owned, slots] = await Promise.all([
      BusinessService.getMyProfiles(ownerId),
      BusinessService.getSlotUsage(ownerId),
    ]);
    setProfiles(owned);
    setSlotsUsed(slots.used);
    setLoaded(true);
  }, [ownerId]);

  useEffect(() => { void load(); }, [load]);

  // Until the read lands, show the create row rather than nothing. A settings
  // list that grows a row a beat after it paints reads as a glitch, and "create"
  // is the correct label for the overwhelmingly common case.
  const canCreate = !loaded || slotsUsed < MAX_BUSINESS_PROFILES;

  return (
    <>
      {profiles.map(profile => (
        <motion.button
          key={profile.id}
          whileHover={{ x: 5 }}
          onClick={() => window.dispatchEvent(
            new CustomEvent(EVENTS.NAVIGATE_TO_BUSINESS_PROFILE, { detail: { id: profile.id } }),
          )}
          className={rowClassName}
        >
          <Briefcase className={iconClassName} />
          <span className={`${labelClassName} truncate`}>
            Manage {profile.businessName}
          </span>
        </motion.button>
      ))}

      {BUSINESS_PROFILES_ENABLED && canCreate && (
        <motion.button
          whileHover={{ x: 5 }}
          onClick={() => window.dispatchEvent(new CustomEvent(EVENTS.NAVIGATE_TO_MY_BUSINESS))}
          className={rowClassName}
        >
          {profiles.length > 0
            ? <Plus className={iconClassName} />
            : <Briefcase className={iconClassName} />}
          <span className={labelClassName}>Create a Business Profile</span>
        </motion.button>
      )}
    </>
  );
}
